import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import "./Product.css";


import psesLogo from "../assets/pses-logo.png";
import mdlsLogo from "../assets/mdls-logo.png";
import ierulesLogo from "../assets/ie-rules-logo.png";
import ieactsLogo from "../assets/ie-acts-logo.png";
import pcnLogo from "../assets/pcn-logo.jpeg";
import pqLogo from "../assets/pq-limits-logo.png";
import piyfLogo from "../assets/piyf-logo.png";
import powerCalciLogo from "../assets/power-calci-logo.png";

const products = [
  {
    title: "PS & ES",
    logo: psesLogo,

    short: "Power Simulation & Engineering Solutions",

    overview:
      "Power Simulation & Engineering Solutions (PS & ES) is an advanced engineering software developed for transmission utilities. It performs conductor ampacity simulation while complying with CEA Norms and IEEE standards.",

    features: [
      "Accurate Ampacity Simulation",
      "Surge Impedance Loading (SIL)",
      "Loss Level Determination",
      "Comparative Conductor Analysis",
      "IEEE & CEA Standard Compliance",
      "Desktop & Mobile Support"
    ],

    applications: [
      "Transmission Utilities",
      "Power System Engineers",
      "Consultants",
      "Renewable Energy Projects"
    ],

    website: "http://www.madtechapps.com"
  },
  {
    title: "M-DLS",
    logo: mdlsLogo,

    short: "Distribution Line Simulation",

    overview:
      "MADTech Distribution Line Simulation (M-DLS) helps distribution utilities analyze HT and LT networks by evaluating voltage profiles, load flow, feeder losses, and system performance for efficient planning.",

    features: [
      "Voltage Profile Analysis",
      "Load Flow Analysis",
      "Feeder Loss Calculation",
      "Benefit Cost Ratio (BCR)",
      "Distribution Network Optimization",
      "Regulatory Reporting"
    ],

    applications: [
      "Distribution Utilities",
      "Power Distribution Engineers",
      "Electrical Consultants",
      "State Electricity Boards"
    ]
  },
  {
    title: "IE Rules",
    logo: ierulesLogo,

    short: "CEA Safety Regulations",

    overview:
      "IE Rules provides engineers with quick access to Indian Electricity Rules, CEA safety regulations, statutory requirements, and engineering standards for safe electrical system design, operation, and maintenance.",

    features: [
      "Indian Electricity Rules",
      "CEA Safety Regulations",
      "Electrical Safety Guidelines",
      "Search by Section",
      "Bookmark Important Rules",
      "Offline Access"
    ],

    applications: [
      "Electrical Engineers",
      "Transmission Utilities",
      "Distribution Utilities",
      "Power Consultants",
      "Industrial Engineers",
      "Government Agencies"
    ]
  },
  {
    title: "IE Acts",

    short: "Indian Electricity Acts",

    overview:
      "IE Acts provides electrical engineers with quick digital access to the Indian Electricity Act, CEA regulations, and other important engineering references for day-to-day professional use.",

    features: [
      "Indian Electricity Act",
      "CEA Regulations",
      "Fast Search",
      "Mobile Friendly",
      "Easy Navigation",
      "Regular Updates"
    ],

    applications: [
      "Electrical Engineers",
      "Consultants",
      "Utilities",
      "Power Companies"
    ],

    logo: require("../assets/ie-acts-logo.png")
  },
  {
    title: "PCN Design",
    logo: pcnLogo,

    short: "Wind Farm Power Collection Network",

    overview:
      "PCN Design is an advanced engineering software for designing and optimizing Wind Farm Power Collection Networks. It assists engineers in developing efficient electrical collection systems by optimizing cable routing, conductor sizing, transformer selection, energy losses, and overall project cost while ensuring compliance with industry standards.",

    features: [
      "Power Collection Network Design",
      "Cable Routing Optimization",
      "Conductor Sizing",
      "Transformer Capacity Selection",
      "Annual Energy Loss Estimation",
      "Voltage Drop Analysis",
      "Cost Optimization",
      "Engineering Reports"
    ],

    applications: [
      "Wind Farm Developers",
      "Renewable Energy Projects",
      "Electrical Consultants",
      "EPC Contractors",
      "Power System Engineers",
      "Utility Companies"
    ],

    website: "http://www.pcn.madtechapps.com"
  },
  {
    title: "PQ Limits",
    logo: pqLogo,

    short: "Power Quality Monitoring",

    overview:
      "PQ Limits is a comprehensive power quality analysis application designed to help utilities, consultants, and industries evaluate electrical network performance in accordance with Grid India, CEA, and IEEE standards. The software enables engineers to assess harmonics, voltage fluctuations, flicker, DC injection, and other critical power quality parameters to ensure reliable and compliant power system operation.",

    features: [
      "Harmonic Distortion Analysis",
      "Voltage Flicker Assessment",
      "DC Injection Evaluation",
      "Voltage & Frequency Monitoring",
      "Grid India & CEA Compliance",
      "IEEE Standard Verification",
      "Power Quality Reporting",
      "Data Visualization & Analysis"
    ],

    applications: [
      "Transmission Utilities",
      "Distribution Utilities",
      "Industrial Power Systems",
      "Renewable Energy Plants",
      "Electrical Consultants",
      "Power Quality Engineers"
    ]
  },
  {
    title: "PIYF",
    logo: piyfLogo,

    short: "Power Information on Your Finger Tips",

    overview:
      "PIYF (Power Information on Your Finger Tips) is a comprehensive engineering reference application that provides quick access to essential power system information, transmission conductor data, electrical calculations, and technical references. It enables engineers to retrieve critical engineering information instantly for faster decision-making and improved operational efficiency.",

    features: [
      "Transmission Conductor Database",
      "Electrical Engineering Calculations",
      "Power System Reference Data",
      "Technical Standards & Information",
      "Quick Engineering Lookup",
      "Offline Data Access",
      "User-Friendly Interface",
      "Regular Technical Updates"
    ],

    applications: [
      "Transmission Utilities",
      "Distribution Utilities",
      "Electrical Engineers",
      "Power System Consultants",
      "Substation Engineers",
      "Field Engineers"
    ]
  },
  {
    title: "Power Calci",
    logo: powerCalciLogo,

    short: "Electrical Engineering Calculator",

    overview:
      "Power Calci is a comprehensive electrical engineering calculator designed to simplify everyday engineering computations. It provides accurate calculations for electrical parameters, cable sizing, voltage drop, fault levels, power factor, transformer ratings, and various power system design calculations, helping engineers save time and improve productivity.",

    features: [
      "Electrical Formula Calculator",
      "Cable Sizing Calculations",
      "Voltage Drop Calculator",
      "Power Factor Calculations",
      "Transformer Rating Calculator",
      "Fault Level Calculations",
      "Unit Conversion Tools",
      "Engineering Reference Formulas"
    ],

    applications: [
      "Electrical Engineers",
      "Power System Consultants",
      "Transmission Utilities",
      "Distribution Utilities",
      "Industrial Engineers",
      "Engineering Students"
    ]
  },
];

export default function Product() {
  const [selected, setSelected] = useState(null);
  const sectionRef = useRef(null);

  // Lock page scroll while modal is open
  useEffect(() => {
    if (selected) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [selected]);

  // Close modal on Escape key
  useEffect(() => {
    if (!selected) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") setSelected(null);
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selected]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const y = 0;
  return (
    <motion.section
      ref={sectionRef}
      className="ipr1-products-section"
    >
      <div className="ipr1-bg-grid"></div>
      <div className="ipr1-bg-glow"></div>
      <div className="ipr1-bg-noise"></div>

      <motion.div
        className="ipr1-products-header"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >

        <span className="ipr1-eyebrow">
          OUR PRODUCTS
        </span>

        <h2 className="ipr1-heading">
          Engineering Software
          <br />
          <span className="ipr1-heading-accent">
            Built for Modern Power Systems
          </span>
        </h2>

        <p className="ipr1-subheading">
          Intelligent engineering applications designed for
          transmission, distribution and renewable energy
          professionals worldwide.
        </p>

      </motion.div>

      <div className="ipr1-products-grid">

        {products.map((item, index) => (

          <motion.div
            className={`ipr1-product-card ipr1-shade-${(index % 4) + 1}`}
            key={index}
            onClick={() => setSelected(item)}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{
              delay: index * 0.15,
              duration: 0.8,
              ease: [0.22, 1, 0.36, 1],
            }}
          >

            <div className="ipr1-product-card-inner">

              <div className="ipr1-product-icon">
                <img
                  src={item.logo}
                  alt={item.title}
                  className="ipr1-product-image"
                />
              </div>

              <div className="ipr1-product-top">

    <span className="ipr1-product-tag">
        {item.category}
    </span>

</div>

<div className="ipr1-product-text">

    <h3 className="ipr1-product-title">
        {item.title}
    </h3>

    <p className="ipr1-product-description">
        {item.short}
    </p>

</div>

              <button className="ipr1-product-button">
                Explore
                <span className="ipr1-product-button-arrow">
                  →
                </span>
              </button>

            </div>

          </motion.div>

        ))}


      </div>

      {selected && (

        <div
          className="ipr1-modal-overlay"
          onClick={() => setSelected(null)}
        >

          <div
            className="ipr1-modal-card"
            onClick={(e) => e.stopPropagation()}
          >

            <button
              className="ipr1-close-btn"
              onClick={() => setSelected(null)}
            >
              ×
            </button>

            <div className="ipr1-modal-header">

              <div className="ipr1-modal-logo">
                <img src={selected.logo} alt={selected.title} />
              </div>

              <div className="ipr1-modal-heading">
                <h2 className="ipr1-modal-title">{selected.title}</h2>
                <p className="ipr1-modal-subtitle">{selected.short}</p>
              </div>

            </div>

            <div className="ipr1-modal-section">

              <h3>Overview</h3>

              <p className="ipr1-modal-description">
                {selected.overview}
              </p>

            </div>

            <div className="ipr1-modal-grid">

              <div className="ipr1-modal-column">

                <h3>Key Features</h3>

                <ul>
                  {selected.features?.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>

              </div>

              <div className="ipr1-modal-column">

                <h3>Applications</h3>

                <ul>
                  {selected.applications?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>

              </div>

            </div>

            {selected.website && (
              <a
                href={selected.website}
                target="_blank"
                rel="noreferrer"
                className="ipr1-visit-btn"
              >
                Visit Website →
              </a>

            )}

          </div>

        </div>

      )}

    </motion.section>
  );
}